import { Activity, Suspense, memo, useEffect, useRef, useState } from "react";
import { Routes, useLocation, useNavigationType } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { activateTab, getTabsState, syncLocation, useTabsState } from "../lib/workspaceTabs";

// c61_673 — QBO-style tabs keep their page ALIVE. Every workspace tab gets its own <Routes> rendered against its
// own address, wrapped in React <Activity>: the active one is visible, the others are hidden (DOM kept, state kept,
// effects/polling paused). Hopping between tabs no longer resets an upload, a search box, a filter or a draft.
// The most recent KEEP_ALIVE hidden tabs stay mounted; older ones reopen fresh from their address.
const KEEP_ALIVE = 8;

// c61_809 — pages are lazy chunks; each tab waits for its own chunk without blanking the others
const ChunkSpinner = () => (
  <div className="min-h-[50vh] flex items-center justify-center" data-testid="tab-chunk-loading">
    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
  </div>
);

const TabTree = memo(({ location, visible, children }) => (
  <Activity mode={visible ? "visible" : "hidden"}>
    <Suspense fallback={<ChunkSpinner />}>
      <Routes location={location}>{children}</Routes>
    </Suspense>
  </Activity>
));

export const WorkspaceTabRoutes = ({ children }) => {
  const live = useLocation();
  // a browser with no tab yet gets one BEFORE the first paint, so the page mounts once under its own tab id
  useState(() => { if (!getTabsState().active) syncLocation(live.pathname, live.search); return null; });
  const { tabs, active } = useTabsState();
  // the ACTIVE tab follows the router (links, in-page nav). Read HERE — outside every per-tab <Routes location>,
  // which shadows useLocation() with the tab's own address. Back / Forward (POP) that lands on another open
  // tab's address re-activates THAT tab instead of dragging the current tab onto its page.
  const navType = useNavigationType();
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current && navType === "POP") {
      const st = getTabsState();
      const hit = st.tabs.find((t) => t.id !== st.active && t.path === live.pathname && (t.search || "") === live.search);
      if (hit) { activateTab(hit.id); return; }
    }
    booted.current = true;
    syncLocation(live.pathname, live.search);
  }, [live.pathname, live.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [recent, setRecent] = useState(() => (active ? [active] : []));
  useEffect(() => {
    if (active) setRecent((r) => (r[0] === active ? r : [active, ...r.filter((id) => id !== active)].slice(0, KEEP_ALIVE * 2)));
  }, [active]);
  const open = new Map(tabs.map((t) => [t.id, t]));
  const keep = new Set([...(active ? [active] : []), ...recent.filter((id) => id !== active && open.has(id))].slice(0, KEEP_ALIVE + 1));
  if (!active || !open.has(active)) return <Suspense fallback={<ChunkSpinner />}><Routes location={live}>{children}</Routes></Suspense>;
  const liveUrl = `${live.pathname}${live.search}`;
  // Each tree renders from ITS tab's address. The active tree takes the router's location object only once the
  // router has actually arrived there — a tab switch commits the new active id before navigate() lands, and
  // rendering the old URL into the new tab's tree for that frame would tear down the page it holds.
  return tabs.filter((t) => keep.has(t.id)).map((t) => {
    const url = `${t.path}${t.search || ""}`;
    const visible = t.id === active;
    return (
      <TabTree key={t.id} visible={visible} location={visible && url === liveUrl ? live : url}>
        {children}
      </TabTree>
    );
  });
};
